import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { Loader2, Plus, RefreshCw, Share2, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  useDeleteProviderWebhook,
  useProviderWebhookDeliveries,
  useProviderWebhooks,
} from '@/hooks/provider/useProviderWebhooks';
import { ProviderWebhooksRequestError } from '@/services/provider/provider-webhooks.service';
import type {
  ProviderWebhookDelivery,
  ProviderWebhookDeliveryStatus,
  ProviderWebhookEndpoint,
} from '@/types/provider-webhooks';
import { ProviderWebhookConfigDialog } from '@/components/settings/ProviderWebhookConfigDialog';

const DELIVERY_LIMIT = 25;

function statusVariant(status: ProviderWebhookDeliveryStatus): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'succeeded':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'processing':
      return 'outline';
    case 'pending':
    default:
      return 'secondary';
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return format(date, 'PPpp');
}

function formatRelativeDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return formatDistanceToNow(date, { addSuffix: true });
}

function DeliveryList({ webhookId }: { webhookId: string }) {
  const { t } = useTranslation();
  const deliveriesQuery = useProviderWebhookDeliveries(webhookId, DELIVERY_LIMIT, true);

  if (deliveriesQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (deliveriesQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="flex items-center justify-between gap-3">
          <span>{t('settings.webhooks.deliveries.loadError', 'Unable to load delivery logs.')}</span>
          <Button size="sm" variant="secondary" onClick={() => deliveriesQuery.refetch()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('common.retry', 'Retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const deliveries = deliveriesQuery.data ?? [];
  if (deliveries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('settings.webhooks.deliveries.empty', 'No deliveries found for this endpoint yet.')}
      </p>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full rounded-md border px-4">
      {deliveries.map((delivery: ProviderWebhookDelivery) => (
        <AccordionItem value={delivery.id} key={delivery.id}>
          <AccordionTrigger className="hover:no-underline">
            <div className="flex w-full flex-wrap items-center gap-3 text-left">
              <Badge variant={statusVariant(delivery.status)}>
                {t(`settings.webhooks.deliveryStatus.${delivery.status}`, delivery.status)}
              </Badge>
              <span className="font-medium">{delivery.eventName}</span>
              <span className="text-sm text-muted-foreground">
                {t('settings.webhooks.deliveries.attempt', 'Attempt {{count}}', { count: delivery.attempt })}
              </span>
              <span className="text-sm text-muted-foreground">{formatDate(delivery.createdAt)}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">{t('settings.webhooks.deliveries.responseStatus', 'Response status')}:</span>{' '}
                  {delivery.responseStatus ?? '—'}
                </p>
                <p>
                  <span className="font-medium">{t('settings.webhooks.deliveries.dispatchedAt', 'Dispatched at')}:</span>{' '}
                  {formatDate(delivery.dispatchedAt)}
                </p>
                <p>
                  <span className="font-medium">{t('settings.webhooks.deliveries.deliveredAt', 'Delivered at')}:</span>{' '}
                  {formatDate(delivery.deliveredAt)}
                </p>
                <p>
                  <span className="font-medium">{t('settings.webhooks.deliveries.nextRetryAt', 'Next retry at')}:</span>{' '}
                  {formatDate(delivery.nextRetryAt)}
                </p>
              </div>
              <div className="space-y-2 text-sm">
                {delivery.errorMessage ? (
                  <div>
                    <p className="mb-1 font-medium">{t('settings.webhooks.deliveries.errorMessage', 'Error message')}</p>
                    <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">{delivery.errorMessage}</pre>
                  </div>
                ) : null}
                {delivery.responseBody ? (
                  <div>
                    <p className="mb-1 font-medium">{t('settings.webhooks.deliveries.responseBody', 'Response body')}</p>
                    <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">{delivery.responseBody}</pre>
                  </div>
                ) : null}
                {delivery.payload ? (
                  <div>
                    <p className="mb-1 font-medium">{t('settings.webhooks.deliveries.payload', 'Payload')}</p>
                    <pre className="max-h-56 overflow-auto rounded bg-muted p-2 text-xs">
                      {JSON.stringify(delivery.payload, null, 2)}
                    </pre>
                  </div>
                ) : null}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function ProviderWebhooksCard() {
  const { t } = useTranslation();
  const webhooksQuery = useProviderWebhooks();
  const deleteMutation = useDeleteProviderWebhook();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedWebhook, setSelectedWebhook] = useState<ProviderWebhookEndpoint | null>(null);
  const [expandedWebhookId, setExpandedWebhookId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const sortedWebhooks = useMemo(() => {
    const list = webhooksQuery.data ?? [];
    return [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [webhooksQuery.data]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setSelectedWebhook(null);
    setDialogOpen(true);
  };

  const openEditDialog = (webhook: ProviderWebhookEndpoint) => {
    setDialogMode('edit');
    setSelectedWebhook(webhook);
    setDialogOpen(true);
  };

  const onDelete = async (webhook: ProviderWebhookEndpoint) => {
    try {
      setPendingDeleteId(webhook.id);
      await deleteMutation.mutateAsync(webhook.id);
      toast.success(t('settings.webhooks.deleteSuccess', 'Webhook endpoint removed.'));
      if (expandedWebhookId === webhook.id) {
        setExpandedWebhookId(null);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('settings.webhooks.deleteError', 'Unable to delete this webhook endpoint.'),
      );
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          {t('settings.webhooks.title', 'Webhook endpoints')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.webhooks.description',
            'Manage webhook destinations and inspect recent delivery attempts.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {t(
              'settings.webhooks.helper',
              'Each enabled endpoint receives subscribed events and can use a shared secret signature.',
            )}
          </p>
          <Button onClick={openCreateDialog} className="self-start sm:self-auto">
            <Plus className="mr-2 h-4 w-4" />
            {t('settings.webhooks.add', 'Add webhook')}
          </Button>
        </div>

        {webhooksQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : null}

        {webhooksQuery.isError ? (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>
                {webhooksQuery.error instanceof ProviderWebhooksRequestError && webhooksQuery.error.status === 403
                  ? t('settings.webhooks.forbidden', 'You do not have permission to manage webhooks.')
                  : t('settings.webhooks.loadError', 'Unable to load webhook endpoints.')}
              </span>
              <Button size="sm" variant="secondary" onClick={() => webhooksQuery.refetch()} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {webhooksQuery.isSuccess && sortedWebhooks.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="font-medium">{t('settings.webhooks.emptyTitle', 'No webhook endpoints configured yet')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(
                'settings.webhooks.emptyDescription',
                'Create at least one endpoint to start receiving webhook events.',
              )}
            </p>
            <Button variant="outline" className="mt-3" onClick={openCreateDialog}>
              {t('settings.webhooks.add', 'Add webhook')}
            </Button>
          </div>
        ) : null}

        {webhooksQuery.isSuccess && sortedWebhooks.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settings.webhooks.table.name', 'Name')}</TableHead>
                  <TableHead>{t('settings.webhooks.table.url', 'Endpoint URL')}</TableHead>
                  <TableHead>{t('settings.webhooks.table.events', 'Events')}</TableHead>
                  <TableHead>{t('settings.webhooks.table.timeout', 'Timeout')}</TableHead>
                  <TableHead>{t('settings.webhooks.table.status', 'Status')}</TableHead>
                  <TableHead>{t('settings.webhooks.table.updated', 'Updated')}</TableHead>
                  <TableHead className="text-right">{t('settings.webhooks.table.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedWebhooks.map((webhook) => {
                  const updatedLabel = formatRelativeDate(webhook.updatedAt) ?? t('settings.webhooks.table.never', 'Never');
                  const isDeleting = deleteMutation.isPending && pendingDeleteId === webhook.id;
                  const isExpanded = expandedWebhookId === webhook.id;

                  return (
                    <Fragment key={webhook.id}>
                      <TableRow>
                        <TableCell className="font-medium">{webhook.name}</TableCell>
                        <TableCell className="max-w-[280px] truncate text-sm text-muted-foreground">
                          {webhook.url}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {t('settings.webhooks.table.eventsCount', '{{count}} selected', {
                              count: webhook.subscribedEvents.length,
                            })}
                          </span>
                        </TableCell>
                        <TableCell>
                          {t('settings.webhooks.table.timeoutSeconds', '{{count}}s', {
                            count: webhook.timeout,
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={webhook.enabled ? 'default' : 'outline'}>
                            {webhook.enabled
                              ? t('settings.webhooks.status.enabled', 'Enabled')
                              : t('settings.webhooks.status.disabled', 'Disabled')}
                          </Badge>
                          {webhook.hasSecret ? (
                            <Badge variant="secondary" className="ml-2">
                              {t('settings.webhooks.status.secretConfigured', 'Secret configured')}
                            </Badge>
                          ) : null}
                          {webhook.hasCustomHeaders ? (
                            <Badge variant="secondary" className="ml-2">
                              {t('settings.webhooks.status.customHeadersConfigured', 'Custom headers')}
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{updatedLabel}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setExpandedWebhookId(isExpanded ? null : webhook.id);
                              }}
                            >
                              {isExpanded ? (
                                <ChevronUp className="mr-2 h-4 w-4" />
                              ) : (
                                <ChevronDown className="mr-2 h-4 w-4" />
                              )}
                              {isExpanded
                                ? t('settings.webhooks.hideDeliveries', 'Hide deliveries')
                                : t('settings.webhooks.viewDeliveries', 'View deliveries')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(webhook)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('common.edit', 'Edit')}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {t('common.delete', 'Delete')}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {t('settings.webhooks.deleteTitle', 'Delete this webhook endpoint?')}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t(
                                      'settings.webhooks.deleteDescription',
                                      'Future events will no longer be delivered to this URL.',
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => void onDelete(webhook)}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    {t('settings.webhooks.confirmDelete', 'Delete webhook')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded ? (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/30">
                            <div className="space-y-3 p-2">
                              <p className="text-sm font-medium">
                                {t('settings.webhooks.deliveries.title', 'Recent deliveries')}
                              </p>
                              <DeliveryList webhookId={webhook.id} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </CardContent>

      <ProviderWebhookConfigDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setSelectedWebhook(null);
          }
        }}
        mode={dialogMode}
        webhook={selectedWebhook}
      />
    </Card>
  );
}
